import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Users, Building2, GraduationCap, Settings, Trophy, TrendingUp, ShieldCheck, CheckCircle2, AlertTriangle, BarChart3, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, LabelList } from "recharts";
import ExportPanel from "@/components/ExportPanel";
import { buildOverviewFromDomains } from "@/utils/executiveDerived";
import ExecutiveMapViewTab from "@/components/ExecutiveMapViewTab";
import ExecutiveIdentityTab from "@/components/ExecutiveIdentityTab";
import ExecutiveInfrastructureTab from "@/components/ExecutiveInfrastructureTab";
import ExecutiveTeacherStaffingTab from "@/components/ExecutiveTeacherStaffingTab";
import ExecutiveOperationalTab from "@/components/ExecutiveOperationalTab";
import { getBackendUrl } from "@/lib/backend";
import { BlockLink, DistrictLink, SchoolLink } from "@/components/DrilldownLink";
import { useSearchParams } from "react-router-dom";
import ExecutiveOverviewTab from "@/components/ExecutiveOverviewTab";
import TeacherCountSchoolsModal from "@/components/TeacherCountSchoolsModal";
import "./ExecutiveDashboard.css";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

import MetricInfoButton from "@/components/MetricInfoButton";
import AiInsightsCard from "@/components/AiInsightsCard";
import { Link } from "react-router-dom";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v) => (typeof v === "number" ? `${v}%` : "0%");

const emptyInsights = (domain) =>
  [
    "## Insights",
    `- No ${domain} data available for the current filters.`,
    "",
    "## Root Cause Signals",
    "- Missing or incomplete records in the selected scope.",
    "",
    "## Recommendations",
    "- Refresh data or broaden filters to include valid records.",
    "",
    "## Priority Action Items",
    `- Validate data import for ${domain} analytics.`,
  ].join("\n");

const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, size = "normal", metricKey = null, onClick = null }) => {
  const colors = { 
    blue: "bg-blue-50 text-blue-600 border-blue-200", 
    green: "bg-emerald-50 text-emerald-600 border-emerald-200", 
    red: "bg-red-50 text-red-600 border-red-200", 
    amber: "bg-amber-50 text-amber-600 border-amber-200", 
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-200"
  };
  const isLarge = size === "large";
  return (
    <Card 
      className={`border-slate-200 ${isLarge ? 'col-span-2' : ''} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} 
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
    >
      <CardContent className={isLarge ? "p-6" : "p-4"}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className={`flex items-center gap-2 ${isLarge ? 'text-sm' : 'text-xs'}`}>
              <p className={`font-medium text-slate-500 uppercase tracking-wider`}>{label}</p>
              {metricKey && (
                <MetricInfoButton 
                  metricKey={metricKey}
                  variant="ghost"
                  size="sm"
                />
              )}
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`font-bold text-slate-900 tabular-nums ${isLarge ? 'text-4xl' : 'text-2xl'}`}>
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className={`text-slate-500 ${isLarge ? 'text-xl' : 'text-lg'}`}>{suffix}</span>}
            </div>
            {description && <p className={`text-slate-400 mt-1 ${isLarge ? 'text-sm' : 'text-xs'}`}>{description}</p>}
          </div>
          {Icon && <div className={`rounded-lg border ${colors[color]} ${isLarge ? 'p-3' : 'p-2'}`}><Icon className={isLarge ? "w-6 h-6" : "w-5 h-5"} strokeWidth={1.5} /></div>}
        </div>
      </CardContent>
    </Card>
  );
};

const GaugeChart = ({ value, label, size = "normal", color, metricKey = null }) => {
  const getColor = (val) => val >= 85 ? "#10b981" : val >= 70 ? "#f59e0b" : "#ef4444";
  const actualColor = color || getColor(value);
  const isLarge = size === "large";
  const radius = isLarge ? 55 : 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(value / 100) * circumference} ${circumference}`;
  const svgSize = isLarge ? 140 : 112;
  const center = svgSize / 2;
  
  return (
    <div className="flex flex-col items-center relative">
      {metricKey && (
        <div className="absolute -top-2 -right-2 z-10">
          <MetricInfoButton 
            metricKey={metricKey}
            variant="outline"
            size="sm"
            className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm"
          />
        </div>
      )}
      <div className={`relative ${isLarge ? 'w-36 h-36' : 'w-28 h-28'}`}>
        <svg className="w-full h-full transform -rotate-90">
          <circle cx={center} cy={center} r={radius} stroke="#e2e8f0" strokeWidth={isLarge ? 12 : 10} fill="none" />
          <circle cx={center} cy={center} r={radius} stroke={actualColor} strokeWidth={isLarge ? 12 : 10} fill="none" strokeDasharray={strokeDasharray} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold ${isLarge ? 'text-2xl' : 'text-xl'}`} style={{ color: actualColor }}>{value}%</span>
        </div>
      </div>
      <p className={`text-slate-600 mt-2 text-center max-w-[120px] ${isLarge ? 'text-base font-medium' : 'text-sm'}`}>{label}</p>
    </div>
  );
};

const DomainCard = ({ title, icon: Icon, index, metrics, color, onClick, metricKey = null }) => {
  const getRAGColor = (val) => val >= 85 ? "bg-emerald-500" : val >= 70 ? "bg-amber-500" : "bg-red-500";
  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer relative" onClick={onClick}>
      {metricKey && (
        <div className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
          <MetricInfoButton 
            metricKey={metricKey}
            variant="ghost"
            size="sm"
          />
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <div className={`w-3 h-3 rounded-full ${getRAGColor(index)}`} title={`Score: ${index}`} />
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-4xl font-bold text-slate-900">{index}</p>
            <p className="text-xs text-slate-500 mt-1">Domain Health</p>
          </div>
          <div className="text-right space-y-1">
            {metrics.map((m, i) => (
              <p key={i} className="text-xs text-slate-600"><span className="font-medium">{m.label}:</span> {m.value}</p>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ExecutiveDashboard = () => {
  const { scope, setScope } = useScope();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [infrastructure, setInfrastructure] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [operational, setOperational] = useState(null);
  const [shi, setShi] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [sqaafOverview, setSqaafOverview] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab) => {
    if (!tab || tab === "overview") {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ tab }, { replace: true });
  };
  const [schoolModalOpen, setSchoolModalOpen] = useState(false);
  const [schoolModalData, setSchoolModalData] = useState(null);
  const [schoolModalLoading, setSchoolModalLoading] = useState(false);
  const [schoolModalTeacherCount, setSchoolModalTeacherCount] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Build scope parameters - the axios interceptor will merge them with localStorage values
      // Also pass district_name, block_name, school_name to help with matching
      const requestParams = {};
      if (scope.districtCode && scope.districtCode.trim()) {
        requestParams.district_code = scope.districtCode.trim();
      }
      if (scope.districtName && scope.districtName.trim()) {
        requestParams.district_name = scope.districtName.trim();
      }
      if (scope.blockCode && scope.blockCode.trim()) {
        requestParams.block_code = scope.blockCode.trim();
      }
      if (scope.blockName && scope.blockName.trim()) {
        requestParams.block_name = scope.blockName.trim();
      }
      const endpoints = [
        { key: "overview", url: `${API}/executive/overview` },
        { key: "identity", url: `${API}/executive/student-identity` },
        { key: "infrastructure", url: `${API}/executive/infrastructure-facilities` },
        { key: "teacher", url: `${API}/executive/teacher-staffing` },
        { key: "operational", url: `${API}/executive/operational-performance` },
        { key: "shi", url: `${API}/executive/school-health-index` },
        { key: "map", url: `${API}/executive/district-map-data` },
      ];
      const [results, sqaafResult] = await Promise.all([
        Promise.allSettled(endpoints.map((e) => axios.get(e.url, { params: requestParams }))),
        axios.get(`${API}/sqaaf/overview`).catch(() => null),
      ]);
      const getData = (idx) => (results[idx].status === "fulfilled" ? results[idx].value?.data : null);
      setOverview(getData(0));
      setIdentity(getData(1));
      setInfrastructure(getData(2));
      setTeacher(getData(3));
      setOperational(getData(4));
      setShi(getData(5));
      setMapData(getData(6));
      setSqaafOverview(sqaafResult?.data || null);
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 2) {
        toast.warning(`Executive dashboard loaded with ${failed} section(s) missing. Import data for full view.`);
      } else if (failed > 0) {
        toast.info(`Some sections have no data yet. Use Data Import to add more sources.`);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error("Failed to load executive dashboard");
      setOverview(null);
      setIdentity(null);
      setInfrastructure(null);
      setTeacher(null);
      setOperational(null);
      setShi(null);
      setMapData(null);
      setSqaafOverview(null);
    } finally {
      setLoading(false);
    }
  }, [scope.version, scope.districtCode, scope.districtName, scope.blockCode, scope.blockName, scope.udiseCode, scope.schoolName]);

  // Executive dashboard aggregates district/block metrics — clear stale school scope from drilldowns.
  useEffect(() => {
    if (scope.udiseCode || scope.schoolName) {
      setScope({ udiseCode: "", schoolName: "" });
    }
  }, [scope.udiseCode, scope.schoolName, setScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSchoolCountClick = async (teacherCount) => {
    setSchoolModalOpen(true);
    setSchoolModalLoading(true);
    setSchoolModalData(null);
    setSchoolModalTeacherCount(teacherCount);

    try {
      const params = new URLSearchParams();
      if (scope.districtCode) params.append("district_code", scope.districtCode);
      if (scope.districtName) params.append("district_name", scope.districtName);
      if (scope.blockCode) params.append("block_code", scope.blockCode);
      if (scope.blockName) params.append("block_name", scope.blockName);
      params.append('teacher_count', teacherCount);
      params.append('limit', '500');
      
      const response = await axios.get(`${API}/executive/schools-by-teacher-count?${params.toString()}`);
      setSchoolModalData(response.data);
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast.error("Failed to load school list");
      setSchoolModalOpen(false);
    } finally {
      setSchoolModalLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>{entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="loading-spinner" /></div>;

  // No data: APIs failed (null) or backend returned all zeros (e.g. no data in DB or scope has no matches)
  const totalSchools = overview?.quick_stats?.total_schools ?? identity?.summary?.total_schools ?? 0;
  const totalStudents = identity?.summary?.total_students ?? 0;
  const shiValue = shi?.summary?.school_health_index ?? 0;
  const hasNoData =
    (!overview && !identity?.summary?.total_students && !shi?.summary?.school_health_index) ||
    (totalSchools === 0 && totalStudents === 0 && shiValue === 0);

  if (hasNoData) {
    return (
      <div className="text-center py-16 px-4 max-w-lg mx-auto">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">No data for Executive Dashboard</h2>
        <p className="text-slate-600 mb-4">
          Data is loaded automatically from Excel when the backend starts. Place the 10 Excel files (01_Aadhaar_Status.xlsx … 10_Classrooms_Toilets.xlsx) in <strong>data/excel</strong> and ensure the backend has finished starting (or restart the backend). If a district/block is selected, try clearing the scope filter.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild variant="default">
            <Link to="/data-import">Data Import</Link>
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  const shiSummary = shi?.summary ?? {};
  const effectiveOverview =
    overview ?? buildOverviewFromDomains(identity, infrastructure, teacher, operational) ?? {};


  const scopeLabel =
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";
  const executiveTabs = [
    { id: "overview", label: "Overview", testId: "tab-overview", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M7 16v-5" /><path d="M12 16V7" /><path d="M17 16v-3" /></svg> },
    { id: "map", label: "Map View", testId: "tab-map", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" /><path d="M9 3v15M15 6v15" /></svg> },
    { id: "identity", label: "Student Overview", testId: "tab-identity", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></svg> },
    { id: "infrastructure", label: "Infrastructure Overview", testId: "tab-infrastructure", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg> },
    { id: "teacher", label: "Teacher Overview", testId: "tab-teacher", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a6 6 0 0 1 12 0v2" /></svg> },
    { id: "operational", label: "Operational Overview", testId: "tab-operational", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87" /></svg> },
  ];

  const scrollToRankings = () => {
    document.getElementById("ed-block-rankings")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="executive-dashboard-page animate-fade-in" data-testid="executive-dashboard">
      <section className="ed-page-header">
        <div>
          <div className="ed-eyebrow">
            {activeTab === "map" ? (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Map intelligence · Maharashtra · Overall School Health
              </>
            ) : activeTab === "identity" ? (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                Student identity intelligence · {scopeLabel} · 2025–26
              </>
            ) : activeTab === "infrastructure" ? (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-4" />
                </svg>
                Infrastructure intelligence · {scopeLabel} · 2025–26
              </>
            ) : activeTab === "teacher" ? (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
                </svg>
                Teacher workforce intelligence · {scopeLabel} · 2025–26
              </>
            ) : activeTab === "operational" ? (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
                Operational intelligence · {scopeLabel} · 2025–26
              </>
            ) : (
              <>
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
                Executive view · {scopeLabel} · 2025–26
              </>
            )}
          </div>
          <h1>Executive Dashboard</h1>
          <p>
            {activeTab === "map"
              ? "District-level map view to compare Overall School Health, identify missing-data regions and drill down from state to district to block-level actions."
              : activeTab === "identity"
                ? "Identity compliance view for Aadhaar coverage, APAAR generation, name-match quality and block-wise improvement planning."
                : activeTab === "infrastructure"
                  ? "Infrastructure readiness view for classrooms, toilets, water availability, electricity and block-level improvement planning."
                  : activeTab === "teacher"
                    ? "Teacher availability, qualification, deployment and staffing-risk view for identifying urgent teacher gaps and quality-improvement actions."
                    : activeTab === "operational"
                      ? "Operational view for data completion, certification, data accuracy, retention and enrolment-gender monitoring across blocks and schools."
                      : "Holistic school health monitoring with clear KPI hierarchy, domain diagnostics, block rankings and action queue for district-level review."}
          </p>
        </div>
        <div className="ed-header-actions">
          <ExportPanel dashboardName="executive-summary" dashboardTitle="Executive Summary" />
          <button type="button" className="ed-primary-action" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="ed-tabs-wrap" aria-label="Dashboard sections">
          <nav className="ed-tabs">
            {executiveTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`ed-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={tab.testId}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <TabsContent value="overview" className="mt-0">
          <ExecutiveOverviewTab
            shiSummary={shiSummary}
            effectiveOverview={effectiveOverview}
            safeShi={shi}
            onDomainClick={setActiveTab}
            onScrollToRankings={scrollToRankings}
            scopeLabel={scopeLabel}
          />
        </TabsContent>

        {/* MAP VIEW */}
        <TabsContent value="map" className="mt-0">
          <ExecutiveMapViewTab mapData={mapData} sqaafOverview={sqaafOverview} />
        </TabsContent>

        {/* STUDENT IDENTITY TAB */}
        <TabsContent value="identity" className="mt-0">
          <ExecutiveIdentityTab identityData={identity} scopeLabel={scopeLabel} />
        </TabsContent>

        {/* INFRASTRUCTURE TAB */}
        <TabsContent value="infrastructure" className="mt-0">
          <ExecutiveInfrastructureTab
            infrastructureData={infrastructure}
            scopeLabel={scopeLabel}
            scopeSchoolName={scope.schoolName}
          />
        </TabsContent>

        {/* TEACHER TAB */}
        <TabsContent value="teacher" className="mt-0">
          <ExecutiveTeacherStaffingTab
            teacherData={teacher}
            scopeLabel={scopeLabel}
            onSchoolCountClick={handleSchoolCountClick}
          />
        </TabsContent>

        {/* OPERATIONAL TAB */}
        <TabsContent value="operational" className="mt-0">
          <ExecutiveOperationalTab operationalData={operational} scopeLabel={scopeLabel} />
        </TabsContent>

      </Tabs>

      <TeacherCountSchoolsModal
        open={schoolModalOpen && [0, 1, 2].includes(schoolModalTeacherCount)}
        onClose={() => setSchoolModalOpen(false)}
        data={schoolModalData}
        loading={schoolModalLoading}
        scopeLabel={scopeLabel}
        teacherCount={schoolModalTeacherCount ?? 0}
      />
    </div>
  );
};

export default ExecutiveDashboard;
